<script lang="ts">
  import { onMount } from 'svelte';
  import type { Snippet } from 'svelte';
  import { DEFAULT_USER_PREFERENCES, DEFAULT_ADAPTIVE_PROFILE } from '@texas42/app-model';
  import type { ToastMessage } from '../components/ui/toast-types.js';
  import { setAppContext, type DialogRequest, type DrawerRequest } from './context.js';
  import { createRouter } from './router.js';
  import { createBrowserPlatformServices } from '../platform/browser-platform.js';
  import {
    bootstrapAppPersistence,
    createMemoryRepositories,
    type AppPersistenceBundle,
  } from '../platform/persistence/create-repositories.js';
  import { storageFailureMessage } from '../platform/persistence/storage-errors.js';
  import {
    createDefaultSessionDeps,
    createGameSessionFactory,
  } from '../session/create-game-session-factory.js';
  import DialogHost from './hosts/DialogHost.svelte';
  import DrawerHost from './hosts/DrawerHost.svelte';
  import OfflineStatusHost from './hosts/OfflineStatusHost.svelte';
  import ToastHost from './hosts/ToastHost.svelte';
  import AppLifecycleHost from './hosts/AppLifecycleHost.svelte';
  import ExpertCampaignHost from './hosts/ExpertCampaignHost.svelte';
  import PreferencesApplier from '../features/settings/PreferencesApplier.svelte';
  import { installGlobalErrorLogging } from '../platform/error-log.js';

  interface Props {
    children?: Snippet;
  }

  let { children }: Props = $props();
  const router = createRouter();

  let persistenceBundle = $state<AppPersistenceBundle>(
    createMemoryRepositories({
      preferences: DEFAULT_USER_PREFERENCES,
      adaptiveProfile: DEFAULT_ADAPTIVE_PROFILE,
    }),
  );
  let persistenceReady = $state(import.meta.env.VITEST === true);

  const platform = createBrowserPlatformServices();

  function buildSessionFactory(bundle: AppPersistenceBundle) {
    return createGameSessionFactory(
      createDefaultSessionDeps({
        matchRepository: bundle.repositories.matches,
        matchPersistence: bundle.matchPersistence,
        adaptiveProfile: bundle.repositories.adaptiveProfile,
      }),
    );
  }

  let sessionFactory = $derived(buildSessionFactory(persistenceBundle));

  let toasts = $state<ToastMessage[]>([]);
  let dialog = $state<DialogRequest | null>(null);
  let drawer = $state<DrawerRequest | null>(null);

  const toast = {
    show(message: Omit<ToastMessage, 'id'>) {
      const id = crypto.randomUUID();
      toasts = [...toasts, { ...message, id }];
      const duration = message.durationMs ?? 5000;
      if (duration > 0) {
        window.setTimeout(() => {
          toasts = toasts.filter((entry) => entry.id !== id);
        }, duration);
      }
    },
    dismiss(id: string) {
      toasts = toasts.filter((entry) => entry.id !== id);
    },
  };

  const dialogService = {
    open(request: Omit<DialogRequest, 'id'>) {
      dialog = { ...request, id: crypto.randomUUID() };
    },
    close() {
      dialog = null;
    },
  };

  const drawerService = {
    open(request: Omit<DrawerRequest, 'id'>) {
      drawer = { ...request, id: crypto.randomUUID() };
    },
    close() {
      drawer = null;
    },
  };

  setAppContext({
    router,
    get repositories() {
      return persistenceBundle.repositories;
    },
    get persistence() {
      return persistenceBundle;
    },
    get matchPersistence() {
      return persistenceBundle.matchPersistence;
    },
    get writeQueue() {
      return persistenceBundle.queue;
    },
    get sessionFactory() {
      return sessionFactory;
    },
    platform,
    toast,
    dialog: dialogService,
    drawer: drawerService,
  });

  onMount(() => {
    const uninstallErrorLogging = installGlobalErrorLogging();

    void (async () => {
      if (import.meta.env.VITEST) {
        return;
      }

      try {
        persistenceBundle = await bootstrapAppPersistence({
          preferences: DEFAULT_USER_PREFERENCES,
          adaptiveProfile: DEFAULT_ADAPTIVE_PROFILE,
        });
      } catch (error) {
        toast.show({
          title: 'Storage unavailable',
          description: storageFailureMessage(error),
          variant: 'warning',
          durationMs: 0,
        });
        persistenceBundle = createMemoryRepositories({
          preferences: DEFAULT_USER_PREFERENCES,
          adaptiveProfile: DEFAULT_ADAPTIVE_PROFILE,
        });
      } finally {
        persistenceReady = true;
      }
    })();

    (globalThis as typeof globalThis & { __texas42?: unknown }).__texas42 = {
      get router() {
        return router;
      },
      get repositories() {
        return persistenceBundle.repositories;
      },
      get persistence() {
        return persistenceBundle;
      },
      get sessionFactory() {
        return sessionFactory;
      },
      get platform() {
        return platform;
      },
      get toast() {
        return toast;
      },
    };

    return () => {
      uninstallErrorLogging();
    };
  });

  function dismissToast(id: string) {
    toast.dismiss(id);
  }

  function closeDialog() {
    dialogService.close();
  }

  function closeDrawer() {
    drawerService.close();
  }
</script>

{#if persistenceReady}
  <PreferencesApplier />
  <AppLifecycleHost />
  <ExpertCampaignHost />
  {@render children?.()}
{/if}

<ToastHost messages={toasts} ondismiss={dismissToast} />
<DialogHost request={dialog} onclose={closeDialog} />
<DrawerHost request={drawer} onclose={closeDrawer} />
<OfflineStatusHost />
